/**
 * EmptyState Component
 * 
 * Displays a centered message when no data is available.
 * Supports both "no data" and "no results" (filtered) states.
 */

import React from 'react';
import type { LucideIcon } from 'lucide-react';

export interface EmptyStateProps {
  /** Icon to display */
  icon: LucideIcon;
  /** Main title */
  title: string;
  /** Description text */
  description: string;
  /** Whether the empty state is due to filtering */
  isFiltered?: boolean;
  /** Optional call-to-action button */
  action?: {
    label: string;
    icon?: LucideIcon;
    onClick: () => void;
  };
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  isFiltered = false,
  action,
}: EmptyStateProps) {
  return (
    <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
      <Icon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
      <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 mb-6">
        {isFiltered 
          ? 'Try adjusting your search or filter criteria.' 
          : description
        }
      </p>
      {action && !isFiltered && (
        <button
          onClick={action.onClick}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mx-auto"
        >
          {action.icon && <action.icon className="w-4 h-4" />}
          {action.label}
        </button>
      )}
    </div>
  );
}
